import get from 'lodash/get';
import isArray from 'lodash/isArray';
import cloneDeep from 'lodash/cloneDeep';
import { SurveyStatus } from './Survey';
import surveyUrls from './surveyUrls';
import ExtensionCollection from './ExtensionCollection';
import { v4 as uuid } from 'uuid';
import { CustomMessages } from '../Components/SurveyBuilder/types';

/**
 * Utitility for cloning and updating fhir questionnaires
 */
export default class QuestionnaireUpdater {
  private _fhirQuestionnaire: fhir.Questionnaire;

  constructor(fhirQuestionnaire: fhir.Questionnaire) {
    this._fhirQuestionnaire = fhirQuestionnaire;
  }

  updateTitle(title: string = ''): QuestionnaireUpdater {
    this._fhirQuestionnaire = {
      ...(this._fhirQuestionnaire || ({} as fhir.Questionnaire)),
      title,
    };

    return this;
  }

  updateDescription(description: string = ''): QuestionnaireUpdater {
    this._fhirQuestionnaire = {
      ...(this._fhirQuestionnaire || ({} as fhir.Questionnaire)),
      description,
    };

    return this;
  }

  updateDate(date: Date = new Date()): QuestionnaireUpdater {
    this._fhirQuestionnaire = {
      ...(this._fhirQuestionnaire || ({} as fhir.Questionnaire)),
      date: date.toISOString(),
    };

    return this;
  }

  updateValueSets(valueSets: fhir.ValueSet[]): QuestionnaireUpdater {
    const safeQuestionnaire =
      this._fhirQuestionnaire || ({} as fhir.Questionnaire);
    const contained = safeQuestionnaire.contained || ([] as any[]);
    const nonValueSetContainedItems = contained.filter(
      (resource = {}) => resource.resourceType !== 'ValueSet',
    );
    this._fhirQuestionnaire = {
      ...safeQuestionnaire,
      contained: [...nonValueSetContainedItems, ...valueSets],
    };

    return this;
  }

  setSurveyStatus(status: SurveyStatus): QuestionnaireUpdater {
    const safeQuestionnaire =
      this._fhirQuestionnaire || ({} as fhir.Questionnaire);
    this._fhirQuestionnaire = {
      ...safeQuestionnaire,
      status,
    };

    return this;
  }

  /**
   * recursively clones questionnaire tree until it reaches the end of the path.  It will then execute the update operation at that tree level
   * @param itemPath
   * @param updateOperation
   */
  _recursiveItemUpdate(
    items: fhir.QuestionnaireItem[],
    itemPath: string[],
    updateOperation: (
      items: fhir.QuestionnaireItem[],
    ) => fhir.QuestionnaireItem[],
  ): fhir.QuestionnaireItem[] {
    if (itemPath.length === 0) return updateOperation(items);

    return items.map(item => {
      const [currentItemLink, ...suffix] = itemPath;

      const linkId = get<fhir.QuestionnaireItem, 'linkId'>(item, 'linkId');
      if (linkId !== currentItemLink) return item;

      const subItems = get<
        fhir.QuestionnaireItem,
        'item',
        fhir.QuestionnaireItem[]
      >(item, 'item', [] as fhir.QuestionnaireItem[]);
      return {
        ...item,
        item: this._recursiveItemUpdate(subItems, suffix, updateOperation),
      };
    });
  }

  /**
   * Appends a new survey item after the targeted link item
   */
  appendItemAfter(
    itemPath: string[] = [],
    newItem: fhir.QuestionnaireItem,
  ): QuestionnaireUpdater {
    const prefix = [...itemPath];
    const targetItem = prefix.pop();

    const updateOperation = (items: fhir.QuestionnaireItem[] = []) => {
      const targetItemIndex = items.findIndex(
        item => item.linkId === targetItem,
      );

      if (targetItemIndex === -1) {
        console.error('Unable to find item to update at path', itemPath);
        return items;
      }

      const copied = [...items];
      copied.splice(targetItemIndex + 1, 0, newItem);
      return copied;
    };

    const sourceItems = get<
      fhir.Questionnaire,
      'item',
      fhir.QuestionnaireItem[]
    >(this._fhirQuestionnaire, 'item', [] as fhir.QuestionnaireItem[]);
    const newItems = this._recursiveItemUpdate(
      sourceItems,
      prefix,
      updateOperation,
    );

    this._fhirQuestionnaire = {
      ...this._fhirQuestionnaire,
      item: newItems,
    };

    return this;
  }

  setChildItem(
    itemPath: string[] = [],
    newItem: fhir.QuestionnaireItem | fhir.QuestionnaireItem[],
  ): QuestionnaireUpdater {
    const normalizedSubItems = isArray(newItem) ? newItem : [newItem];
    const updateOperation = () => normalizedSubItems;
    const sourceItems = get<
      fhir.Questionnaire,
      'item',
      fhir.QuestionnaireItem[]
    >(this._fhirQuestionnaire, 'item', [] as fhir.QuestionnaireItem[]);
    const newItems = this._recursiveItemUpdate(
      sourceItems,
      itemPath,
      updateOperation,
    );

    this._fhirQuestionnaire = {
      ...this._fhirQuestionnaire,
      item: newItems,
    };

    return this;
  }

  addModifierExtensionToItem(
    itemPath: string[] = [],
    extension: fhir.Extension,
  ): QuestionnaireUpdater {
    const prefix = [...itemPath];
    const targetItem = prefix.pop();

    const updateOperation = (
      items: fhir.QuestionnaireItem[],
    ): fhir.QuestionnaireItem[] => {
      return items.map(item => {
        if (item?.linkId !== targetItem) return item;

        const extensionCollection =
          ExtensionCollection.initFromBackboneElement(item);
        extensionCollection.upsertExtension(extension);
        return {
          ...(item || {}),
          modifierExtension: extensionCollection.extensions,
        } as fhir.QuestionnaireItem;
      });
    };

    const newItems = this._recursiveItemUpdate(
      this._fhirQuestionnaire?.item || [],
      prefix,
      updateOperation,
    );

    this._fhirQuestionnaire = {
      ...this._fhirQuestionnaire,
      item: newItems,
    };

    return this;
  }

  replaceItem(
    itemPath: string[] = [],
    newItem: fhir.QuestionnaireItem,
  ): QuestionnaireUpdater {
    const prefix = [...itemPath];
    const targetItem = prefix.pop();

    const updateOperation = (items: fhir.QuestionnaireItem[] = []) => {
      const targetItemIndex = items.findIndex(
        item => item.linkId === targetItem,
      );

      if (targetItemIndex === -1) {
        console.error('Unable to find item to update at path', itemPath);
        return items;
      }

      return items.map((item, index) =>
        index === targetItemIndex ? newItem : item,
      );
    };

    const sourceItems = get<
      fhir.Questionnaire,
      'item',
      fhir.QuestionnaireItem[]
    >(this._fhirQuestionnaire, 'item', [] as fhir.QuestionnaireItem[]);
    const newItems = this._recursiveItemUpdate(
      sourceItems,
      prefix,
      updateOperation,
    );

    this._fhirQuestionnaire = {
      ...this._fhirQuestionnaire,
      item: newItems,
    };

    return this;
  }

  updatePropertiesOnItem(
    itemPath: string[] = [],
    newItem: Omit<fhir.QuestionnaireItem, 'linkId' | 'type'>,
  ): QuestionnaireUpdater {
    const prefix = [...itemPath];
    const targetItemLink = prefix.pop();

    const updateOperation = (items: fhir.QuestionnaireItem[] = []) => {
      const targetItemIndex = items.findIndex(
        item => item.linkId === targetItemLink,
      );

      if (targetItemIndex === -1) {
        console.error('Unable to find item to update at path', itemPath);
        return items;
      }

      const targetItem = items[targetItemIndex];
      const updatedItem = {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        linkId: targetItem.linkId,
        ...targetItem,
        ...(newItem || {}),
      } as fhir.QuestionnaireItem;

      return items.map((item, index) =>
        index === targetItemIndex ? updatedItem : item,
      );
    };

    const sourceItems = get<
      fhir.Questionnaire,
      'item',
      fhir.QuestionnaireItem[]
    >(this._fhirQuestionnaire, 'item', [] as fhir.QuestionnaireItem[]);
    const newItems = this._recursiveItemUpdate(
      sourceItems,
      prefix,
      updateOperation,
    );

    this._fhirQuestionnaire = {
      ...this._fhirQuestionnaire,
      item: newItems,
    };

    return this;
  }

  swapItems(item1Path: string[] = [], item2Link: string): QuestionnaireUpdater {
    const prefix = [...item1Path];
    const item1Link = prefix.pop();

    const updateOperation = (items: fhir.QuestionnaireItem[] = []) => {
      const item1Index = items.findIndex(item => item.linkId === item1Link);
      const item2Index = items.findIndex(item => item.linkId === item2Link);

      if (item1Index === -1) {
        console.error('Unable to find item to update at path', [
          ...prefix,
          item1Index,
        ]);
        return items;
      }

      if (item2Index === -1) {
        console.error('Unable to find item to update at path', [
          ...prefix,
          item2Index,
        ]);
        return items;
      }

      const item1 = items[item1Index];
      const item2 = items[item2Index];

      return items.map((item, index) => {
        if (index === item1Index) {
          return item2; // item2 -> item1 index
        } else if (index === item2Index) {
          return item1; // item1 -> item2 index
        } else {
          return item;
        }
      });
    };

    const sourceItems = get<
      fhir.Questionnaire,
      'item',
      fhir.QuestionnaireItem[]
    >(this._fhirQuestionnaire, 'item', [] as fhir.QuestionnaireItem[]);
    const newItems = this._recursiveItemUpdate(
      sourceItems,
      prefix,
      updateOperation,
    );

    this._fhirQuestionnaire = {
      ...this._fhirQuestionnaire,
      item: newItems,
    };

    return this;
  }

  removeItem(itemPath: string[]): QuestionnaireUpdater {
    const prefix = [...itemPath];
    const itemLink = prefix.pop();

    const updateOperation = (items: fhir.QuestionnaireItem[] = []) => {
      return items.filter(item => item.linkId !== itemLink);
    };

    const sourceItems = get<
      fhir.Questionnaire,
      'item',
      fhir.QuestionnaireItem[]
    >(this._fhirQuestionnaire, 'item', [] as fhir.QuestionnaireItem[]);
    const newItems = this._recursiveItemUpdate(
      sourceItems,
      prefix,
      updateOperation,
    );

    this._fhirQuestionnaire = {
      ...this._fhirQuestionnaire,
      item: newItems,
    };

    return this;
  }

  /**
   * Applies the link to the target questionnaire item and then recursively applies links to its children
   */
  _recursiveReplaceLink(item: fhir.QuestionnaireItem, newLink: string) {
    if (!item) {
      console.error(
        'Tried to apply a link to a non-existent questionnaire item',
      );
      return;
    }

    item.linkId = newLink;
    const existingChildren = item.item || ([] as fhir.QuestionnaireItem[]);

    // if no children present, no need to recurse further
    if (!existingChildren || !existingChildren.length) return item;

    const childrenItemsWithNewLinks = existingChildren.map(existingChild =>
      this._recursiveReplaceLink(existingChild, uuid()),
    );

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    item.item = childrenItemsWithNewLinks;
    return item;
  }

  /**
   * Recursively removes the modifier extensions from the given questionnaire item
   */
  _recursiveRemoveModifierExtension(
    item: fhir.QuestionnaireItem,
    targetExtensionUrl: string,
  ): fhir.QuestionnaireItem {
    if (!item) {
      console.error(
        'Tried to remove a modifer extension to a non-existent questionnaire item',
      );
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return;
    }

    const returnItem: fhir.QuestionnaireItem = {
      ...item,
    };

    if (item?.modifierExtension?.length) {
      const updatedModifierExtensions = (item?.modifierExtension || []).filter(
        _ => _?.url !== targetExtensionUrl,
      );

      if (updatedModifierExtensions.length) {
        returnItem.modifierExtension = updatedModifierExtensions;
      } else {
        delete returnItem.modifierExtension;
      }
    }

    if (item?.item?.length) {
      const updatedSubItems = (item?.item || []).map(_ =>
        this._recursiveRemoveModifierExtension(_, targetExtensionUrl),
      );

      returnItem.item = updatedSubItems;
    }

    return returnItem;
  }

  /**
   * Clones the target questionnaire item replacing the link IDs with appropriate IDs defined by the provided root id
   * @param itemPath - the path of the target item to clone
   * @param clonedItemId - the root link id of the newly cloned item
   */
  cloneItemAndAppend(
    itemPath: string[],
    itemSiblingLinks: string[],
    targetItem: fhir.QuestionnaireItem,
  ): QuestionnaireUpdater {
    if (!targetItem) {
      console.error('Attempted to clone a non-existent item');
      return this;
    }

    if (!itemSiblingLinks || itemSiblingLinks.length === 0) {
      console.error(
        'Cannot generate a new link without any reference sibling links',
      );
      return this;
    }

    // clone the target item and replace all the links with links as the new reference
    const clonedQuestionnaireItem = cloneDeep(targetItem);
    const newItemWithLinks = this._recursiveReplaceLink(
      clonedQuestionnaireItem,
      uuid(),
    );

    // append "(copy)" to avoid possible confusion.  Only do it if the item has a textual display defined
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (newItemWithLinks.text) {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      newItemWithLinks.text = `${newItemWithLinks.text} (copy)`;
    }

    /*
      if this is a versioned item, copy the target an sub  without the "updates forbidden" modifier extension.
      source items should prohibit updates, but cloned items should allow updates
    */
    const newItemWithUpdatesAllowed = this._recursiveRemoveModifierExtension(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      newItemWithLinks,
      surveyUrls.updatesForbidden,
    );

    const prefix = [...itemPath];
    prefix.pop(); // remove the clone target link.  We want to drill down to the sibling level

    const updateOperation = (items: fhir.QuestionnaireItem[] = []) => {
      // add the new cloned item to the list
      return [...items, newItemWithUpdatesAllowed];
    };

    const sourceItems = get<
      fhir.Questionnaire,
      'item',
      fhir.QuestionnaireItem[]
    >(this._fhirQuestionnaire, 'item', [] as fhir.QuestionnaireItem[]);
    const newItems = this._recursiveItemUpdate(
      sourceItems,
      prefix,
      updateOperation,
    );

    this._fhirQuestionnaire = {
      ...this._fhirQuestionnaire,
      item: newItems,
    };

    return this;
  }

  /**
   * Completely replaces the questionnaire item with the supplied items
   * @param item
   */
  setRootQuestionnaireItem(
    item: fhir.QuestionnaireItem[],
  ): QuestionnaireUpdater {
    this._fhirQuestionnaire = {
      ...this._fhirQuestionnaire,
      item,
    };

    return this;
  }

  /**
   * Sets a flag determining whether or not this survey should be directly sent to subjects
   * @param isInternalSurvey
   */
  setInternalStatus(isInternalSurvey: boolean): QuestionnaireUpdater {
    const meta = get<fhir.Questionnaire, 'meta', fhir.Meta>(
      this._fhirQuestionnaire,
      'meta',
      {},
    );
    const tag = get<fhir.Meta, 'tag', fhir.Coding[]>(meta, 'tag', []);
    const tagsWithoutInternalFlag = tag.filter(tag => {
      const system = get<fhir.Coding, 'system'>(tag, 'system');
      return system !== surveyUrls.internalSurvey;
    });

    let newTags = tagsWithoutInternalFlag;
    if (isInternalSurvey) {
      newTags = [
        ...tagsWithoutInternalFlag,
        { system: surveyUrls.internalSurvey },
      ];
    }

    this._fhirQuestionnaire = {
      ...this._fhirQuestionnaire,
      meta: {
        ...meta,
        tag: newTags,
      },
    };

    return this;
  }

  /**
   * Sets a flag determining whether or not this survey permits additional responses after completion
   * @param allowsResponseResubmission
   */
  setAllowsResponseResubmission(
    allowsResponseResubmission: boolean,
  ): QuestionnaireUpdater {
    const tags =
      this._fhirQuestionnaire?.meta?.tag?.filter(tag => {
        return tag.system !== surveyUrls.allowsResponseResubmission;
      }) ?? [];

    if (allowsResponseResubmission) {
      tags.push({
        system: surveyUrls.allowsResponseResubmission,
        code: 'true',
      });
    }

    this._fhirQuestionnaire.meta = this._fhirQuestionnaire.meta || {};
    this._fhirQuestionnaire.meta.tag = tags;

    return this;
  }

  /**
   * Toggles the survey-level repeating-groups opt-in flag.  The flag is
   * written to modifierExtension (NOT the root extension, which is dropped
   * when the questionnaire is saved), but the read path treats a matching
   * entry in either location as enabled, so both are stripped here — this
   * normalizes surveys that carry the flag on root extension and ensures
   * disabling actually disables.  With the flag set, group items with
   * repeats=true render as repeated instance cards ("Add another") instead
   * of the legacy single-section behavior.
   */
  setRepeatingGroupsEnabled(enabled: boolean): QuestionnaireUpdater {
    const extension = (this._fhirQuestionnaire.extension || []).filter(
      ext => ext.url !== surveyUrls.enableRepeatingGroups,
    );
    const modifierExtension = (
      this._fhirQuestionnaire.modifierExtension || []
    ).filter(ext => ext.url !== surveyUrls.enableRepeatingGroups);

    if (enabled) {
      modifierExtension.push({ url: surveyUrls.enableRepeatingGroups });
    }

    this._fhirQuestionnaire = {
      ...this._fhirQuestionnaire,
      extension,
      modifierExtension,
    };

    return this;
  }

  /**
   * Toggles hiding Questionnaire.description on the completing header.
   * Written to modifierExtension (root extension is dropped on save). The
   * read path treats either location as enabled, so both are stripped here.
   */
  setHidesDescription(enabled: boolean): QuestionnaireUpdater {
    const extension = (this._fhirQuestionnaire.extension || []).filter(
      ext => ext.url !== surveyUrls.hideDescription,
    );
    const modifierExtension = (
      this._fhirQuestionnaire.modifierExtension || []
    ).filter(ext => ext.url !== surveyUrls.hideDescription);

    if (enabled) {
      modifierExtension.push({ url: surveyUrls.hideDescription });
    }

    this._fhirQuestionnaire = {
      ...this._fhirQuestionnaire,
      extension,
      modifierExtension,
    };

    return this;
  }

  /**
   * Toggles hiding the visible required asterisk on the completing view.
   * Written to modifierExtension (root extension is dropped on save). The
   * read path treats either location as enabled, so both are stripped here.
   */
  setHidesRequiredAsterisks(enabled: boolean): QuestionnaireUpdater {
    const extension = (this._fhirQuestionnaire.extension || []).filter(
      ext => ext.url !== surveyUrls.hideRequiredAsterisks,
    );
    const modifierExtension = (
      this._fhirQuestionnaire.modifierExtension || []
    ).filter(ext => ext.url !== surveyUrls.hideRequiredAsterisks);

    if (enabled) {
      modifierExtension.push({ url: surveyUrls.hideRequiredAsterisks });
    }

    this._fhirQuestionnaire = {
      ...this._fhirQuestionnaire,
      extension,
      modifierExtension,
    };

    return this;
  }

  /**
   * Toggles completion progress on collapsible-section headers. Root
   * extension is stripped while modifierExtension is the persisted source.
   */
  /**
   * Toggles Member-page clinic quick launch. Root extension is stripped;
   * modifierExtension is the persisted source.
   *
   * The extension's value (valueInteger/valueDecimal) is an authored sort
   * weight for the quick-launch table, so enabling keeps an existing
   * extension object rather than rebuilding a bare flag. Disabling removes
   * the extension entirely, weight included — unchecking the box is a
   * deliberate author action, and a re-enable starts unweighted.
   */
  setQuickLaunchEnabled(enabled: boolean): QuestionnaireUpdater {
    const existingQuickLaunch = [
      ...(this._fhirQuestionnaire.modifierExtension || []),
      ...(this._fhirQuestionnaire.extension || []),
    ].find(ext => ext.url === surveyUrls.quickLaunch);

    const extension = (this._fhirQuestionnaire.extension || []).filter(
      ext => ext.url !== surveyUrls.quickLaunch,
    );
    const modifierExtension = (
      this._fhirQuestionnaire.modifierExtension || []
    ).filter(ext => ext.url !== surveyUrls.quickLaunch);

    if (enabled) {
      modifierExtension.push(
        existingQuickLaunch ?? { url: surveyUrls.quickLaunch },
      );
    }

    this._fhirQuestionnaire = {
      ...this._fhirQuestionnaire,
      extension,
      modifierExtension,
    };

    return this;
  }

  setShowsSectionCompletionStatus(enabled: boolean): QuestionnaireUpdater {
    const extension = (this._fhirQuestionnaire.extension || []).filter(
      ext => ext.url !== surveyUrls.sectionCompletionStatus,
    );
    const modifierExtension = (
      this._fhirQuestionnaire.modifierExtension || []
    ).filter(ext => ext.url !== surveyUrls.sectionCompletionStatus);

    if (enabled) {
      modifierExtension.push({ url: surveyUrls.sectionCompletionStatus });
    }

    this._fhirQuestionnaire = {
      ...this._fhirQuestionnaire,
      extension,
      modifierExtension,
    };

    return this;
  }

  upsertTag(tag: fhir.Coding): QuestionnaireUpdater {
    const existingTags = this._fhirQuestionnaire?.meta?.tag || [];
    const withNewTag = [
      ...existingTags.filter(_ => _.system !== tag.system),
      tag,
    ];

    this._fhirQuestionnaire = {
      ...this._fhirQuestionnaire,
      meta: {
        ...(this._fhirQuestionnaire?.meta || {}),
        tag: withNewTag,
      },
    };
    return this;
  }

  removeTag(system: string): QuestionnaireUpdater {
    const existingTags = this._fhirQuestionnaire?.meta?.tag || [];
    const newTags = existingTags.filter(_ => _.system !== system);

    this._fhirQuestionnaire = {
      ...this._fhirQuestionnaire,
      meta: {
        ...(this._fhirQuestionnaire?.meta || {}),
        tag: newTags,
      },
    };
    return this;
  }

  /**
   * Adds a modifier extension to the root survey.  This will replace existing extension with same url
   */
  addSurveyModifierExtension(extension: fhir.Extension): QuestionnaireUpdater {
    this.removeSurveyModifierExtension(extension?.url);
    this._fhirQuestionnaire = {
      ...this._fhirQuestionnaire,
      modifierExtension: [
        ...(this._fhirQuestionnaire.modifierExtension || []),
        extension,
      ],
    };
    return this;
  }

  removeSurveyModifierExtension(extensionUrl: string): QuestionnaireUpdater {
    const existingExtensions = this._fhirQuestionnaire.modifierExtension || [];
    const withoutTargetExtension = existingExtensions.filter(
      _ => _?.url !== extensionUrl,
    );
    this._fhirQuestionnaire = {
      ...this._fhirQuestionnaire,
      modifierExtension: withoutTargetExtension,
    };
    return this;
  }

  get fhirQuestionnaire(): fhir.Questionnaire {
    return this._fhirQuestionnaire;
  }

  updateCustomMessage(
    messageType: CustomMessages,
    message?: string,
  ): QuestionnaireUpdater {
    const extensionUrl = surveyUrls.customMessage(messageType);
    this.removeSurveyModifierExtension(extensionUrl);

    if (message) {
      this.addSurveyModifierExtension({
        url: extensionUrl,
        valueString: message,
      });
    }

    return this;
  }
}
